import { useState, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { AIControlPanel } from "../components/AIControlPanel";
import { Bot, Plus, Trash2, Pencil, Send, MessageCircleQuestion, CheckCircle2, FlaskConical } from "lucide-react";

const EMPTY_T = { question: "", question_samples: [], answer: "", category: "General", branch_ids: [], approved: true };

export default function WhatsAppAI() {
  const { user } = useAuth();
  const isOwner = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(EMPTY_T);
  const [tEditId, setTEditId] = useState(null);
  const [samplesText, setSamplesText] = useState("");
  const [sim, setSim] = useState({ text: "", reply: null, path: null, loading: false });
  const [replyBox, setReplyBox] = useState({});
  const [saveTpl, setSaveTpl] = useState(null);

  const loadAll = () => {
    api.get("/wa-ai/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/wa-ai/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/wa-ai/unanswered", { params: { status: "open" } }).then((r) => setUnanswered(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const openCreateT = () => { setTForm(EMPTY_T); setSamplesText(""); setTEditId(null); setTOpen(true); };
  const openEditT = (t) => { setTForm({ ...t }); setSamplesText((t.question_samples || []).join("\n")); setTEditId(t.id); setTOpen(true); };
  const saveT = async () => {
    if (!tForm.question.trim() || !tForm.answer.trim()) return toast.error("Question and answer are required");
    const payload = { ...tForm, question_samples: samplesText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (tEditId) await api.put(`/wa-ai/templates/${tEditId}`, payload);
      else await api.post("/wa-ai/templates", payload);
      toast.success("Template saved"); setTOpen(false); loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delT = async (id) => { try { await api.delete(`/wa-ai/templates/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const runSim = async () => {
    if (!sim.text.trim()) return;
    setSim((s) => ({ ...s, loading: true, reply: null, path: null }));
    try {
      const { data } = await api.post("/wa-ai/simulate", { text: sim.text });
      setSim((s) => ({ ...s, loading: false, reply: data.reply, path: data.path }));
      loadAll();
    } catch (e) { setSim((s) => ({ ...s, loading: false })); toast.error("AI is taking long — try again or a simpler question"); }
  };

  const sendReply = async (u) => {
    const answer = (replyBox[u.id] || "").trim();
    if (!answer) return toast.error("Type a reply first");
    try { await api.post(`/wa-ai/unanswered/${u.id}/reply`, { answer }); toast.success("Reply sent" + (status?.mode === "simulation" ? " (simulated)" : "")); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const resolveU = async (u) => { try { await api.put(`/wa-ai/unanswered/${u.id}/resolve`); loadAll(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const doSaveTemplate = async () => {
    if (!saveTpl.answer.trim()) return toast.error("Answer required");
    try {
      await api.post(`/wa-ai/unanswered/${saveTpl.id}/to-template`, { question: saveTpl.question, answer: saveTpl.answer, category: saveTpl.category || "General" });
      toast.success("Saved as approved template"); setSaveTpl(null); loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="whatsapp-ai-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-3xl flex items-center gap-2"><Bot className="text-primary" /> WhatsApp AI</h1>
        {status && <Badge variant="outline" className={status.mode === "live" ? "border-green-500/50 text-green-400" : "border-amber-500/50 text-amber-400"} data-testid="wa-mode-badge">{status.mode === "live" ? "LIVE (Meta connected)" : "Simulation mode"}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground mb-6">Auto-answers customer WhatsApp questions using live salon data first, then your approved replies, then AI — with human review when unsure. {status && `Model: ${status.model}.`}</p>

      <Tabs defaultValue="unanswered">
        <TabsList className="mb-4">
          <TabsTrigger value="unanswered" data-testid="tab-unanswered">Unanswered {unanswered.length > 0 && <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center">{unanswered.length}</span>}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Reply Templates / FAQ</TabsTrigger>
          <TabsTrigger value="tester" data-testid="tab-tester">AI Tester</TabsTrigger>
          {isOwner && <TabsTrigger value="control" data-testid="tab-ai-control">AI Control &amp; Instructions</TabsTrigger>}
        </TabsList>

        {/* Unanswered */}
        <TabsContent value="unanswered">
          {!unanswered.length && <p className="text-muted-foreground text-sm flex items-center gap-2"><CheckCircle2 size={16} className="text-green-400" /> No unanswered questions. All caught up!</p>}
          <div className="space-y-3">
            {unanswered.map((u) => (
              <Card key={u.id} className="p-4 bg-card border-border" data-testid={`unanswered-${u.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">From {u.from} · {(u.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    <p className="font-medium mt-1 flex items-center gap-2"><MessageCircleQuestion size={16} className="text-amber-400" /> {u.question}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => resolveU(u)} data-testid={`resolve-${u.id}`}>Mark resolved</Button>
                </div>
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Input placeholder="Type the correct answer…" value={replyBox[u.id] || ""} onChange={(e) => setReplyBox({ ...replyBox, [u.id]: e.target.value })} className="bg-secondary" data-testid={`reply-input-${u.id}`} />
                  <Button onClick={() => sendReply(u)} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid={`send-reply-${u.id}`}><Send size={14} className="mr-1" /> Reply</Button>
                  {isOwner && <Button variant="outline" className="border-primary/50 text-primary" onClick={() => setSaveTpl({ id: u.id, question: u.question, answer: replyBox[u.id] || "", category: "General" })} data-testid={`save-template-${u.id}`}>Save as Template</Button>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-muted-foreground">Owner-approved answers. The AI matches natural-language questions to these.</p>
            {isOwner && <Button onClick={openCreateT} data-testid="add-template-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Template</Button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <Card key={t.id} className="p-4 bg-card border-border" data-testid={`template-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Badge variant="outline" className="mb-1 text-xs">{t.category || "General"}</Badge>
                    <p className="font-medium">{t.question}</p>
                  </div>
                  {isOwner && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditT(t)} data-testid={`edit-template-${t.id}`}><Pencil size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => delT(t.id)} data-testid={`delete-template-${t.id}`}><Trash2 size={14} /></Button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2">{t.answer}</p>
                <p className="text-xs text-primary/70 mt-2">Used {t.usage_count || 0}×{t.branch_ids?.length ? ` · ${t.branch_ids.length} branch(es)` : " · All branches"}</p>
              </Card>
            ))}
            {!templates.length && <p className="text-muted-foreground text-sm">No templates yet.</p>}
          </div>
        </TabsContent>

        {/* Tester */}
        <TabsContent value="tester">
          <Card className="p-5 bg-card border-border max-w-2xl" data-testid="ai-tester">
            <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2"><FlaskConical size={16} className="text-primary" /> Test what the AI would reply to a customer (no message is actually sent).</p>
            <div className="flex gap-2">
              <Input placeholder="e.g. how much is a gold facial?" value={sim.text} onChange={(e) => setSim({ ...sim, text: e.target.value })} className="bg-secondary" data-testid="sim-input" onKeyDown={(e) => e.key === "Enter" && runSim()} />
              <Button onClick={runSim} disabled={sim.loading} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="sim-run">{sim.loading ? "Thinking…" : "Ask"}</Button>
            </div>
            {sim.reply !== null && (
              <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-4" data-testid="sim-result">
                <Badge variant="outline" className="mb-2 text-xs">source: {sim.path}</Badge>
                <p className="text-sm">{sim.reply}</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {isOwner && (
          <TabsContent value="control" data-testid="ai-control-section">
            <AIControlPanel />
          </TabsContent>
        )}
      </Tabs>

      {/* Template create/edit */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{tEditId ? "Edit" : "Add"} Reply Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Question *</Label><Input data-testid="template-question" value={tForm.question} onChange={(e) => setTForm({ ...tForm, question: e.target.value })} className="bg-secondary mt-1" /></div>
            <div><Label className="text-xs">Other ways customers ask (one per line)</Label><textarea data-testid="template-samples" value={samplesText} onChange={(e) => setSamplesText(e.target.value)} rows={3} className="w-full bg-secondary mt-1 rounded-md border border-border p-2 text-sm" /></div>
            <div><Label className="text-xs">Approved Answer *</Label><textarea data-testid="template-answer" value={tForm.answer} onChange={(e) => setTForm({ ...tForm, answer: e.target.value })} rows={3} className="w-full bg-secondary mt-1 rounded-md border border-border p-2 text-sm" /></div>
            <div><Label className="text-xs">Category</Label><Input data-testid="template-category" value={tForm.category} onChange={(e) => setTForm({ ...tForm, category: e.target.value })} className="bg-secondary mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={saveT} data-testid="template-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save unanswered as template */}
      <Dialog open={!!saveTpl} onOpenChange={(o) => !o && setSaveTpl(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Save as Approved Template</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Only Owner approval can create a permanent template. The AI can never auto-approve its own answers.</p>
          <div className="space-y-3">
            <div><Label className="text-xs">Question</Label><Input value={saveTpl?.question || ""} onChange={(e) => setSaveTpl({ ...saveTpl, question: e.target.value })} className="bg-secondary mt-1" data-testid="savetpl-question" /></div>
            <div><Label className="text-xs">Answer</Label><textarea value={saveTpl?.answer || ""} onChange={(e) => setSaveTpl({ ...saveTpl, answer: e.target.value })} rows={3} className="w-full bg-secondary mt-1 rounded-md border border-border p-2 text-sm" data-testid="savetpl-answer" /></div>
            <div><Label className="text-xs">Category</Label><Input value={saveTpl?.category || ""} onChange={(e) => setSaveTpl({ ...saveTpl, category: e.target.value })} className="bg-secondary mt-1" data-testid="savetpl-category" /></div>
          </div>
          <DialogFooter><Button onClick={doSaveTemplate} data-testid="savetpl-confirm" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">Save Template</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
